import React, { Fragment } from 'react';
import styled from 'styled-components';
import {
  FadeUp,
  ListHeading,
  List,
  ListItem
} from 'components/ProductSidebar/menus/menuComponents';
import ProductCard from 'components/ProductSidebar/ProductCard/ProductCard';
import { Category, CategoryWidget, ProductListConfig } from 'rangeConfig/@types';
import { useIntl } from 'react-intl';
import { RenderProps } from './withProducts';
import { useSelector } from 'react-redux';
import { isMobile } from 'utils/platform';
import NoProductsOnFilterMessage from 'components/NoProductsOnFilterMessage/NoProductsOnFilterMessage';
import { useCore } from 'contexts/core';
import { e2e } from 'utils/queryUtils';
import NoneOptionCard from 'components/ProductSidebar/ProductCard/NoneOptionCard';
import { getVisibleProductsForGroup } from 'ducks/products';
import Box from 'ui/Box';
import { getTranslatedTitle } from 'products';
import { CategoryData } from './getCategoriesData';
import { RenderProps as CategoryRenderProps } from 'rangeConfig/shared/widgets/withCategoryFilter';
import SkipButton from 'ui/SkipButton/SkipButton';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';

type Props = RenderProps & CategoryRenderProps;

const Wrapper = styled.div`
  padding: 0;
`;

const MobileHeader = styled.div`
  padding: ${({ theme }) => theme.space.xs} 0;
  background: white;
  overflow: hidden;
`;

const StickyDesktopHeader = styled.div`
  position: sticky;
  z-index: 12;
  background: white;
  overflow: hidden;
  margin: 0 -${({ theme }) => theme.space.xxl};
  padding: 0 ${({ theme }) => theme.space.xxl};
`;

type RenderListProps = {
  productListConfig: ProductListConfig;
  categoryData: CategoryData;
  selectedGroupId: string;
};

const RenderListOfProducts = ({
  productListConfig,
  categoryData,
  selectedGroupId
}: RenderListProps) => {
  const coreApi = useCore();
  const category = productListConfig.categories.find((c: Category) => c.id === categoryData.id);
  const shouldCluster = !!productListConfig.cluster.length;

  const cardTitle = category?.cardsTitle ?? productListConfig.cardsTitle;
  const cardSubtitle = category?.cardsSubtitle ?? productListConfig.cardsSubtitle;

  const showInfoBtn =
    productListConfig.hideInfoButton !== undefined ? !productListConfig.hideInfoButton : true;
  const showPrice = productListConfig.hidePrice !== undefined ? !productListConfig.hidePrice : true;
  const showAvailability =
    productListConfig.hideAvailability !== undefined ? !productListConfig.hideAvailability : true;

  return categoryData.productsCluster.map((products, listIndex) =>
    shouldCluster
      ? !!products.length && (
          <ListItem key={products[0].coreProductId}>
            <ProductCard
              infoButtonAriaLabel={productListConfig.infoButtonAriaLabel}
              action="auto-adapt"
              cardTitle={cardTitle}
              cardSubtitle={cardSubtitle}
              behaviors={productListConfig.behaviors}
              product={products[0]}
              cluster={products}
              showInfoBtn={showInfoBtn}
              showPrice={showPrice}
              showAvailability={showAvailability}
              selectedGroupId={selectedGroupId}
              productCardIcon={productListConfig.productCardIcon}
              selected={productListConfig.selected}
              mainImage={productListConfig.productCardMainImage}
              hoverImage={productListConfig.productCardHoverImage}
              listIndex={listIndex}
              aria-disabled={
                !!productListConfig.categories.find(
                  c =>
                    c.id === categoryData.id &&
                    c.shouldDisableProducts &&
                    c.shouldDisableProducts(coreApi)
                )
              }
              disabled={
                !!productListConfig.categories.find(
                  c =>
                    c.id === categoryData.id &&
                    c.shouldDisableProducts &&
                    c.shouldDisableProducts(coreApi)
                )
              }
            />
          </ListItem>
        )
      : products.map((product, i) => (
          <ListItem key={product.coreProductId}>
            <ProductCard
              infoButtonAriaLabel={productListConfig.infoButtonAriaLabel}
              action="add"
              product={product}
              selectedGroupId={selectedGroupId}
              cardTitle={cardTitle}
              cardSubtitle={cardSubtitle}
              productCardIcon={productListConfig.productCardIcon}
              selected={productListConfig.selected}
              behaviors={productListConfig.behaviors}
              mainImage={productListConfig.productCardMainImage}
              hoverImage={productListConfig.productCardHoverImage}
              showInfoBtn={showInfoBtn}
              showPrice={showPrice}
              showAvailability={showAvailability}
              listIndex={i}
              disabled={
                !!productListConfig.categories.find(
                  c =>
                    c.id === categoryData.id &&
                    c.shouldDisableProducts &&
                    c.shouldDisableProducts(coreApi)
                )
              }
            />
          </ListItem>
        ))
  );
};

export const ProductList = ({
  categoryIds,
  productListConfig,
  categoriesData,
  selectedGroup,
  selectedGroupId
}: Props) => {
  const { formatMessage } = useIntl();
  const coreApi = useCore();
  const visibleProducts = useSelector(getVisibleProductsForGroup);

  const filteredCategoriesData = categoryIds
    ? categoriesData.filter(p => categoryIds.includes(p.id))
    : categoriesData;

  const groupHasFilters =
    selectedGroup?.mainMenu.filters && selectedGroup?.mainMenu.filters?.length > 0;

  const Header = isMobile() || e2e() ? MobileHeader : StickyDesktopHeader;

  const visibleProductsInCategory = (categoryData: CategoryData) =>
    categoryData.productsCluster.some(productArray => productArray.length > 0);

  const shouldHideCategoryWidget = (widget: CategoryWidget) => {
    return (widget.hidden && widget.hidden(coreApi)) || false;
  };

  const shouldShowCategoryTitle = (categoryData: CategoryData) =>
    !isMobile() &&
    categoryData.title &&
    (visibleProductsInCategory(categoryData) ||
      categoryData.widgets.some(widget => widget.alwaysShow));

  if (groupHasFilters && visibleProducts.length === 0) {
    return <NoProductsOnFilterMessage />;
  }

  return (
    <>
      <FadeUp id="product-list-top">
        {filteredCategoriesData.length > 0 &&
          filteredCategoriesData.map(categoryData => (
            <Fragment key={categoryData.id}>
              <Header data-hasfilters={groupHasFilters} style={{ top: 88 }}>
                {shouldShowCategoryTitle(categoryData) && (
                  <ListHeading type={ThemeFontStyleTypeEnum.bodyLarge}>
                    {getTranslatedTitle(
                      categoryData.title as string,
                      categoryData.productsCluster[0][0],
                      formatMessage
                    )}
                  </ListHeading>
                )}
              </Header>
              {categoryData.widgets.map((widget: CategoryWidget, i: number) => {
                const Widget = widget.component;
                return (
                  ((visibleProductsInCategory(categoryData) && !shouldHideCategoryWidget(widget)) ||
                    widget.alwaysShow) && (
                    <Box
                      key={`category-widget-${categoryData.id + i}`}
                      data-testid={`category-widget-${categoryData.id + i}`}
                    >
                      <Widget products={categoryData.productsCluster.flat()} />
                    </Box>
                  )
                );
              })}
              {!productListConfig.categories.find((c: Category) => c.id === categoryData.id)
                ?.noSkipButton &&
                !categoryData.productsCluster.every(products => products.length === 0) && (
                  <SkipButton
                    m="1rem"
                    dataTestId={'button-skip-product-listing-' + categoryData.id}
                    skipToId={'skipListingFocusElement' + categoryData.id}
                    text={formatMessage({ id: 'kompis.carouselSkipListing' })}
                  />
                )}
              {visibleProductsInCategory(categoryData) && (
                <Wrapper>
                  <List>
                    {productListConfig.noneOption && (
                      <ListItem key="productListNoneOption">
                        <NoneOptionCard
                          noneOption={productListConfig.noneOption}
                          currentMenu={selectedGroupId}
                        />
                      </ListItem>
                    )}
                    {productListConfig.categories.find((c: Category) => c.id === categoryData.id)
                      ?.noneOption && (
                      <ListItem key="noneOption">
                        <NoneOptionCard
                          noneOption={
                            productListConfig.categories.find(
                              (c: Category) => c.id === categoryData.id
                            )?.noneOption
                          }
                          currentMenu={selectedGroupId}
                        />
                      </ListItem>
                    )}
                    <RenderListOfProducts
                      selectedGroupId={selectedGroupId}
                      categoryData={categoryData}
                      productListConfig={productListConfig}
                    />
                  </List>
                  {!productListConfig.categories.find((c: Category) => c.id === categoryData.id)
                    ?.noSkipButton &&
                    !categoryData.productsCluster.every(products => products.length === 0) && (
                      <div id={'skipListingFocusElement' + categoryData.id} tabIndex={-1}></div>
                    )}
                </Wrapper>
              )}
            </Fragment>
          ))}
      </FadeUp>
    </>
  );
};

export default ProductList;
